import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut, type User } from 'firebase/auth';
import { get, push, ref, set, update } from 'firebase/database';
import { Building2, Loader2, Users, Check } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { auth, database } from '../services/firebase';
import { searchOrganizations, type OrganizationMatch } from '../services/organizations';

type LocationState = {
  returnTo?: string;
};

type UserProfile = {
  needsOrganizationSetup?: boolean;
  organizationId?: string;
  organizationMembershipStatus?: string;
  pendingOrganizationId?: string | null;
};

type PlacesAddressComponent = {
  shortText?: string;
  longText?: string;
  types?: string[];
};

type PlaceResult = {
  id: string;
  displayName?: { text?: string };
  formattedAddress?: string;
  addressComponents?: PlacesAddressComponent[];
  nationalPhoneNumber?: string;
};

const GOOGLE_PLACES_KEY = 'AIzaSyDEGMqyA5NtiozNRFBimsc5q1rZOs0KTaU';

const PLACE_TYPE_FILTERS: Record<
  string,
  {
    includedTypes?: string[];
    keyword?: string;
  }
> = {
  'Veterinary clinic': {},
  'Veterinary hospital': {},
  Boarding: { keyword: 'pet boarding' },
  Groomer: { keyword: 'pet groomer' },
  'Training / Behavior': { keyword: 'dog training' },
  'Rescue / Shelter': { includedTypes: ['animal_shelter'], keyword: 'animal rescue' },
  'Mobile services': { keyword: 'mobile vet pet care' },
  Pharmacy: { includedTypes: ['pharmacy'], keyword: 'pet pharmacy' },
  'Pet store / Boutique': { includedTypes: ['pet_store'], keyword: 'pet store' },
  'Dog walker': { keyword: 'dog walker' },
  'Pet daycare': { keyword: 'pet daycare' },
};

export default function CreateOrganization() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const returnTo = locationState?.returnTo;

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return localStorage.getItem('theme') === 'dark';
  });

  const [organizationName, setOrganizationName] = useState('');
  const [organizationDisplayName, setOrganizationDisplayName] = useState('');
  const [organizationType, setOrganizationType] = useState('');
  const [organizationTypeOther, setOrganizationTypeOther] = useState('');
  const [locationName, setLocationName] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [locationCity, setLocationCity] = useState('');
  const [locationStateCode, setLocationStateCode] = useState('');
  const [locationZip, setLocationZip] = useState('');
  const [locationPhone, setLocationPhone] = useState('');
  const [locationContactEmail, setLocationContactEmail] = useState('');
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>(null);
  const [locationResults, setLocationResults] = useState<PlaceResult[]>([]);
  const [locationSearchLoading, setLocationSearchLoading] = useState(false);
  const [locationSearchError, setLocationSearchError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [matchingOrganizations, setMatchingOrganizations] = useState<OrganizationMatch[]>([]);
  const [matchingLoading, setMatchingLoading] = useState(false);
  const [matchingError, setMatchingError] = useState<string | null>(null);
  const [showClinicForm, setShowClinicForm] = useState(false);
  const [debouncedOrganizationName, setDebouncedOrganizationName] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      setCurrentUser(user);
      setAuthChecked(true);
      if (!user) {
        navigate('/signin', {
          replace: true,
          state: { returnTo: '/organization/create' },
        });
      }
    });
    return unsubscribe;
  }, [navigate]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme === 'dark') {
      setIsDarkMode(true);
    }
    if (storedTheme === 'light') {
      setIsDarkMode(false);
    }
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setProfile(null);
      return;
    }
    const profileRef = ref(database, `users/${currentUser.uid}`);
    void get(profileRef)
      .then(snapshot => {
        const value = snapshot.val() as UserProfile | null;
        setProfile(value);
        if (value && value.needsOrganizationSetup === false && value.organizationId) {
          navigate(returnTo ?? '/organization', { replace: true });
        }
      })
      .catch(error => {
        console.warn('Failed to read user profile for organization setup', error);
      });
  }, [currentUser, navigate, returnTo]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedOrganizationName(organizationName);
    }, 300);
    return () => {
      clearTimeout(handle);
    };
  }, [organizationName]);

  const searchTerm = useMemo(() => {
    const name = debouncedOrganizationName.trim();
    return name.length >= 3 ? name : '';
  }, [debouncedOrganizationName]);

  useEffect(() => {
    let isActive = true;

    if (!searchTerm) {
      setMatchingOrganizations([]);
      setMatchingError(null);
      setMatchingLoading(false);
      return () => {
        isActive = false;
      };
    }

    setMatchingLoading(true);
    setMatchingError(null);

    void searchOrganizations(searchTerm)
      .then(results => {
        if (!isActive) {
          return;
        }
        setMatchingOrganizations(results);
      })
      .catch(error => {
        console.warn('Failed to search existing organizations', error);
        if (!isActive) {
          return;
        }
        setMatchingOrganizations([]);
        setMatchingError('We were unable to look up existing organizations. Please try again.');
      })
      .finally(() => {
        if (isActive) {
          setMatchingLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [searchTerm]);

  const cardClass = `${isDarkMode ? 'bg-slate-900/60 text-slate-100 ring-slate-800' : 'bg-white text-slate-900 ring-slate-200'} rounded-lg p-8 ring-1`;
  const labelClass = `text-sm font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`;
  const inputClass = `mt-2 w-full rounded-xl border px-4 py-3 text-base shadow-sm transition focus:outline-none focus:ring-2 ${isDarkMode ? 'border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500' : 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-blue-600 focus:ring-blue-600'}`;
  const primaryButtonClass = `inline-flex w-fit items-center justify-center gap-2 rounded-xl px-4 py-3 text-base font-semibold shadow-sm transition ${isDarkMode ? 'bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-200/60' : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500/40'}`;
  const helperClass = `text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`;
  const matchCardClass = `${isDarkMode ? 'border-slate-700 bg-slate-900/60' : 'border-slate-200 bg-white'} flex flex-col gap-3 rounded-2xl border p-5 shadow-sm`;
  const matchButtonClass = `inline-flex w-fit items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition ${isDarkMode ? 'border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700' : 'border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200'}`;
  const secondaryButtonClass = `inline-flex w-fit items-center justify-center gap-2 rounded-xl border px-4 py-3 text-base font-semibold transition ${isDarkMode ? 'border-slate-600 bg-slate-900/60 text-slate-100 hover:bg-slate-800' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100'}`;

  const handleThemeToggle = () => {
    setIsDarkMode(prev => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem('theme', next ? 'dark' : 'light');
      }
      return next;
      });
  };

  const extractAddressComponent = (components: PlacesAddressComponent[] | undefined, type: string | string[]) => {
    const targetTypes = Array.isArray(type) ? type : [type];
    const match = components?.find(component => component.types?.some(value => targetTypes.includes(value)));
    return (match?.shortText ?? match?.longText ?? '').trim();
  };

  const parsePlaceAddress = (place: PlaceResult) => {
    const comps = place.addressComponents ?? [];
    const street = [extractAddressComponent(comps, 'street_number'), extractAddressComponent(comps, 'route')]
      .filter(Boolean)
      .join(' ')
      .trim();
    const city = extractAddressComponent(comps, ['locality', 'postal_town', 'sublocality']);
    const state = extractAddressComponent(comps, 'administrative_area_level_1');
    const zip = extractAddressComponent(comps, 'postal_code');

    let address = street;
    let resolvedCity = city;
    let resolvedState = state;
    let resolvedZip = zip;

    if ((!address || !resolvedCity || !resolvedState || !resolvedZip) && place.formattedAddress) {
      const parts = place.formattedAddress.split(',').map(part => part.trim());
      const [streetLine, cityLine, stateZipLine] = parts;
      if (!address && streetLine) address = streetLine;
      if (!resolvedCity && cityLine) resolvedCity = cityLine;
      if (stateZipLine) {
        const match = stateZipLine.match(/([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)/);
        if (match) {
          if (!resolvedState) resolvedState = match[1];
          if (!resolvedZip) resolvedZip = match[2];
        }
      }
    }

    if (!address || !resolvedCity || !resolvedState || !resolvedZip) {
      return null;
    }

    return {
      address,
      city: resolvedCity,
      state: resolvedState.toUpperCase(),
      zip: resolvedZip,
    };
  };

  useEffect(() => {
    if (!showClinicForm) {
      return;
    }
    if (locationSearchQuery.trim().length === 0) {
      setLocationSearchQuery(organizationDisplayName || organizationName);
    }
  }, [locationSearchQuery, organizationDisplayName, organizationName, showClinicForm]);

  useEffect(() => {
    if (!showClinicForm) {
      return;
    }
    const query = locationSearchQuery.trim();
    if (query.length < 3) {
      setLocationResults([]);
      setLocationSearchError(null);
      setLocationSearchLoading(false);
      return;
    }

    let isActive = true;
    const controller = new AbortController();
    setLocationSearchLoading(true);
    setLocationSearchError(null);

    const timeout = setTimeout(() => {
      const selectedType =
        organizationType === 'Other' && organizationTypeOther.trim()
          ? organizationTypeOther.trim()
          : organizationType;
      const filter = selectedType ? PLACE_TYPE_FILTERS[selectedType] : undefined;
      const textQuery = filter?.keyword ? `${query} ${filter.keyword}` : query;

      fetch('https://places.googleapis.com/v1/places:searchText', {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': GOOGLE_PLACES_KEY,
          'X-Goog-FieldMask': [
            'places.id',
            'places.displayName',
            'places.formattedAddress',
            'places.addressComponents',
            'places.nationalPhoneNumber',
          ].join(','),
        },
        body: JSON.stringify({
          textQuery,
          maxResultCount: 8,
          languageCode: 'en',
          includedTypes: filter?.includedTypes,
        }),
      })
        .then(async response => {
          if (!response.ok) {
            const message = await response.text();
            throw new Error(message || 'Places search failed');
          }
          return response.json() as Promise<{ places?: PlaceResult[] }>;
        })
        .then(data => {
          if (!isActive) return;
          setLocationResults(data.places ?? []);
        })
        .catch(error => {
          if (!isActive || controller.signal.aborted) return;
          console.warn('Failed to search places for organization location', error);
          setLocationResults([]);
          setLocationSearchError('Unable to search locations right now.');
        })
        .finally(() => {
          if (isActive) setLocationSearchLoading(false);
        });
    }, 250);

    return () => {
      isActive = false;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [locationSearchQuery, organizationType, organizationTypeOther, showClinicForm]);

  const handleSelectPlace = (place: PlaceResult) => {
    const address = parsePlaceAddress(place);
    const fallbackName = organizationDisplayName || organizationName || '';
    setLocationName((place.displayName?.text ?? fallbackName).trim() || fallbackName);
    if (address) {
      setLocationAddress(address.address);
      setLocationCity(address.city);
      setLocationStateCode(address.state);
      setLocationZip(address.zip);
    }
    setLocationPhone(place.nationalPhoneNumber ?? '');
    setLocationContactEmail('');
    setSelectedPlaceId(place.id);
    setSelectedPlace(place);
  };

  const handleSignIn = () => {
    navigate('/signin', {
      state: returnTo
        ? {
          returnTo,
        }
        : { returnTo: '/organization/create' },
    });
  };

  const handleCreateAccount = () => {
    navigate('/register', {
      state: returnTo
        ? {
          returnTo,
        }
        : { returnTo: '/organization/create' },
    });
  };

  const handleLogout = () => {
    const logoutReturnTo = returnTo ?? '/organization/create';
    void signOut(auth)
      .then(() => {
        navigate('/signin', {
          replace: true,
          state: { returnTo: logoutReturnTo },
        });
      })
      .catch(error => {
        console.warn('Failed to sign out', error);
      });
  };

  const validateFields = () => {
    if (!organizationDisplayName.trim()) {
      return 'Organization name is required.';
    }
    if (!organizationType.trim()) {
      return 'Organization type is required.';
    }
    if (organizationType === 'Other' && !organizationTypeOther.trim()) {
      return 'Please enter your organization type.';
    }
    if (!locationName.trim() || !locationAddress.trim() || !locationCity.trim() || !locationStateCode.trim() || !locationZip.trim()) {
      return 'Select a location from Google Places to continue.';
    }
    return null;
  };

  const handleCreateOrganization = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!currentUser) {
      setSubmitError('You need to be signed in to create an organization.');
      return;
    }

    const validationMessage = validateFields();
    if (validationMessage) {
      setSubmitError(validationMessage);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const resolvedOrganizationType =
        organizationType === 'Other' ? organizationTypeOther.trim() : organizationType.trim();
      if (!selectedPlaceId || !selectedPlace) {
        throw new Error('Select a location before creating the organization.');
      }
      const organizationRef = push(ref(database, 'organizations'));
      const organizationId = organizationRef.key;

      if (!organizationId) {
        throw new Error('Unable to generate organization id.');
      }

      const timestamp = Date.now();

      await set(organizationRef, {
        id: organizationId,
        name: organizationDisplayName.trim(),
        type: resolvedOrganizationType,
        stripeCustomerId: null,
        subscriptionId: null,
        isEnterprise: false,
        createdAt: timestamp,
        createdBy: currentUser.uid,
      });

      const normalizedState = locationStateCode.trim().toUpperCase();

      await set(ref(database, `organizationUsers/${organizationId}/${currentUser.uid}`), {
        organizationId,
        userId: currentUser.uid,
        status: 'Active',
        role: 'Administrator',
        joinedAt: timestamp,
        displayName: currentUser.displayName ?? null,
        email: currentUser.email ?? null,
      });

      const clinicId = selectedPlaceId;
      const locationRef = ref(database, `locations/${clinicId}`);
      const locationSnap = await get(locationRef);
      const existingLocation = locationSnap.exists() ? (locationSnap.val() as Record<string, unknown>) : null;

      if (existingLocation) {
        const existingOrg = typeof existingLocation.organizationId === 'string' ? existingLocation.organizationId : null;
        const existingCreator = typeof existingLocation.createdBy === 'string' ? existingLocation.createdBy : null;
        if (existingOrg && existingOrg !== organizationId) {
          setSubmitError('This place is already linked to another organization.');
          setSubmitting(false);
          return;
        }
        if (!existingOrg && existingCreator && existingCreator !== currentUser.uid) {
          setSubmitError('This place was added by another user. Choose a different location or contact support.');
          setSubmitting(false);
          return;
        }
      }

      const locationPayload = {
        id: clinicId,
        organizationId,
        name: locationName.trim(),
        address: locationAddress.trim(),
        city: locationCity.trim(),
        state: normalizedState,
        zip: locationZip.trim(),
        phone: locationPhone.trim() || null,
        email: locationContactEmail.trim() || null,
        type: resolvedOrganizationType,
        organizationType: resolvedOrganizationType,
        status: 'active',
        createdAt:
          existingLocation && typeof existingLocation.createdAt === 'number'
            ? existingLocation.createdAt
            : timestamp,
        createdBy:
          existingLocation && typeof existingLocation.createdBy === 'string'
            ? (existingLocation.createdBy as string)
            : currentUser.uid,
        updatedAt: timestamp,
        placeId: clinicId,
        source: 'google_places',
      };

      await set(locationRef, locationPayload);

      await update(ref(database), {
        [`organizationLocations/${organizationId}/${clinicId}`]: {
          addedAt: timestamp,
          status: 'active',
          updatedAt: timestamp,
        },
      });

      await update(ref(database, `users/${currentUser.uid}`), {
        needsOrganizationSetup: false,
        organizationId,
        organizationMembershipStatus: 'Active',
      });

      navigate(returnTo ?? '/organization', { replace: true });
    } catch (error) {
      console.warn('Failed to create organization', error);
      setSubmitError('Unable to create organization. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinueWithoutMatch = () => {
    setSubmitError(null);
    if (!organizationDisplayName.trim() && organizationName.trim()) {
      setOrganizationDisplayName(organizationName.trim());
    }
    setShowClinicForm(true);
  };

  const handleJoinExisting = async (organization: OrganizationMatch) => {
    if (!currentUser) {
      setSubmitError('You need to be signed in to request access.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      await set(ref(database, `organizationUsers/${organization.id}/${currentUser.uid}`), {
        organizationId: organization.id,
        userId: currentUser.uid,
        status: 'Pending',
        role: 'Member',
        requestedAt: Date.now(),
        displayName: currentUser.displayName ?? null,
        email: currentUser.email ?? null,
      });

      await update(ref(database, `users/${currentUser.uid}`), {
        needsOrganizationSetup: false,
        organizationMembershipStatus: 'Pending',
        pendingOrganizationId: organization.id,
      });

      setProfile(prev => ({
        ...(prev ?? {}),
        needsOrganizationSetup: false,
        organizationId: prev?.organizationId,
        organizationMembershipStatus: 'Pending',
        pendingOrganizationId: organization.id,
      }));
    } catch (error) {
      console.warn('Failed to request organization access', error);
      setSubmitError('Unable to request access. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!authChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-900 text-slate-100">
        <div className="flex items-center gap-3 text-lg font-semibold">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          Checking your session...
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return null;
  }

  const disableForm = submitting || profile?.organizationMembershipStatus === 'Pending';
  const hasOrganizationAccess = Boolean(profile?.organizationId);
  const isCreateDisabled = disableForm || !selectedPlaceId;

  return (
    <DashboardLayout
      isDarkMode={isDarkMode}
      onToggleTheme={handleThemeToggle}
      currentUser={currentUser}
      onSignIn={handleSignIn}
      onCreateAccount={handleCreateAccount}
      onLogout={handleLogout}
      needsOrganizationSetup={profile?.needsOrganizationSetup}
      hasOrganizationAccess={hasOrganizationAccess}
      organizationMembershipStatus={profile?.organizationMembershipStatus}
    >
      <div className="space-y-6">
        <section className={cardClass}>
          <div className="mb-8 flex flex-col gap-3 text-left">
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-blue-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-500">
              <Building2 className="h-4 w-4" aria-hidden="true" />
              Organization Setup
            </span>
            <h1 className="text-3xl font-bold">Tell us about your organization</h1>
            <p className={helperClass}>
              We&apos;ll create your organization so your team can collaborate inside Happy Tail Tracker.
            </p>
          </div>
          <form className="flex flex-col gap-6" onSubmit={handleCreateOrganization}>
            {!showClinicForm ? (
              <>
                <div className="space-y-4 rounded-2xl border border-dashed border-slate-200/60 p-4 md:p-6">
                  <h2 className="text-lg font-semibold">Organization details</h2>
                  <div>
                    <label className={labelClass} htmlFor="organizationName">
                      Organization name
                    </label>
                    <input
                      id="organizationName"
                      className={inputClass}
                      type="text"
                      placeholder="Happy Tails Veterinary Group"
                      autoComplete="organization"
                      value={organizationName}
                      onChange={event => setOrganizationName(event.target.value)}
                      disabled={disableForm}
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide">
                    <Users className="h-4 w-4" aria-hidden="true" />
                    Possible matches
                  </div>
                  {matchingError ? (
                    <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-500">
                      {matchingError}
                    </p>
                  ) : matchingLoading ? (
                    <div className="flex items-center gap-2 text-sm text-blue-500">
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      Searching for existing organizations...
                    </div>
                  ) : matchingOrganizations.length === 0 ? (
                    <p className={helperClass}>
                      {searchTerm
                        ? 'We could not find any organizations that match that name. Double-check the spelling or ask your administrator for an invite.'
                        : 'If your organization already uses Happy Tail Tracker, start typing its name to request access instead.'}
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {matchingOrganizations.map(organization => (
                        <div key={organization.id} className={matchCardClass}>
                          <div className="space-y-1">
                            <p className="text-base font-semibold">{organization.name}</p>
                            <p className={helperClass}>
                              {organization.isEnterprise ? 'Enterprise account' : 'Standard account'}
                            </p>
                            {organization.subscriptionConfigured ? (
                              <p className={helperClass}>Subscription configured</p>
                            ) : (
                              <p className={helperClass}>Billing setup pending</p>
                            )}
                          </div>
                          <button
                            type="button"
                            className={matchButtonClass}
                            onClick={() => handleJoinExisting(organization)}
                            disabled={disableForm}
                          >
                            {submitting ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                Sending request...
                              </>
                            ) : (
                              <>
                                <Users className="h-4 w-4" aria-hidden="true" />
                                Request to join (pending approval)
                              </>
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : null}

            {!showClinicForm ? (
              <div className="flex flex-col gap-3 rounded-2xl border border-dashed border-slate-200/60 p-4 md:p-6">
                <h2 className="text-lg font-semibold">No matches?</h2>
                <p className={helperClass}>
                  Continue to create a new organization with a primary location.
                </p>
                <button
                  type="button"
                  className={secondaryButtonClass}
                  onClick={handleContinueWithoutMatch}
                  disabled={disableForm}
                >
                  Continue without a match
                </button>
              </div>
            ) : (
              <>
                <div className="space-y-4 rounded-2xl border border-dashed border-slate-200/60 p-4 md:p-6">
                  <h2 className="text-lg font-semibold">Primary location</h2>
                  <p className={helperClass}>
                    We&apos;ll create your first location. You can add more locations later.
                  </p>
                  <div>
                    <label className={labelClass} htmlFor="organizationDisplayName">
                      Organization name
                    </label>
                    <input
                      id="organizationDisplayName"
                      className={inputClass}
                      type="text"
                      placeholder="Happy Tails Veterinary Group"
                      autoComplete="organization"
                      value={organizationDisplayName}
                      onChange={event => setOrganizationDisplayName(event.target.value)}
                      disabled={disableForm}
                      required
                    />
                  </div>
                  <div>
                    <label className={labelClass} htmlFor="organizationType">
                      Organization type
                    </label>
                    <select
                      id="organizationType"
                      className={inputClass}
                      value={organizationType}
                      onChange={event => {
                        const value = event.target.value;
                        setOrganizationType(value);
                        if (value !== 'Other') {
                          setOrganizationTypeOther('');
                        }
                      }}
                      disabled={disableForm}
                      required
                    >
                      <option value="">Select a type</option>
                      <option value="Veterinary clinic">Veterinary clinic</option>
                      <option value="Veterinary hospital">Veterinary hospital</option>
                      <option value="Boarding">Boarding</option>
                      <option value="Groomer">Groomer</option>
                      <option value="Training / Behavior">Training / Behavior</option>
                      <option value="Rescue / Shelter">Rescue / Shelter</option>
                      <option value="Mobile services">Mobile services</option>
                      <option value="Pharmacy">Pharmacy</option>
                      <option value="Pet store / Boutique">Pet store / Boutique</option>
                      <option value="Dog walker">Dog walker</option>
                      <option value="Pet daycare">Pet daycare</option>
                      <option value="Other">Other</option>
                    </select>
                  {organizationType === 'Other' ? (
                    <div className="mt-3">
                      <label className={labelClass} htmlFor="organizationTypeOther">
                        Describe your organization type
                      </label>
                        <input
                          id="organizationTypeOther"
                          className={inputClass}
                          type="text"
                          placeholder="e.g., Hydrotherapy center"
                          value={organizationTypeOther}
                          onChange={event => setOrganizationTypeOther(event.target.value)}
                          disabled={disableForm}
                          required
                        />
                      </div>
                    ) : null}
                  </div>
                  {organizationType ? (
                    <div className="space-y-2">
                      <label className={labelClass} htmlFor="locationSearch">
                        Find your primary location
                      </label>
                      <input
                        id="locationSearch"
                        className={inputClass}
                        type="text"
                        placeholder="Start typing your business name or address"
                      value={locationSearchQuery}
                      onChange={event => {
                        setLocationSearchQuery(event.target.value);
                        setSelectedPlaceId(null);
                        setSelectedPlace(null);
                      }}
                      disabled={disableForm}
                      required
                    />
                      <p className={helperClass}>We&apos;ll prefill details from Google Places.</p>
                      <div className="space-y-3">
                        {locationSearchLoading ? (
                          <div className="flex items-center gap-2 text-sm text-blue-500">
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                            Searching...
                          </div>
                        ) : locationSearchError ? (
                          <p className="text-sm text-red-500">{locationSearchError}</p>
                        ) : locationResults.length > 0 ? (
                          locationResults.map(place => {
                            const address =
                              place.formattedAddress ??
                              place.addressComponents?.map(c => c.shortText ?? c.longText ?? '').join(', ');
                            const isSelected = place.id === selectedPlaceId;
                            return (
                              <button
                                type="button"
                                key={place.id}
                                className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left shadow-sm transition ${
                                  isDarkMode
                                    ? isSelected
                                      ? 'border-blue-400 bg-blue-500/20'
                                      : 'border-slate-700 bg-slate-900/60 hover:bg-slate-800'
                                    : isSelected
                                      ? 'border-blue-500 bg-blue-50'
                                      : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
                                }`}
                                onClick={() => handleSelectPlace(place)}
                                disabled={disableForm}
                              >
                                <div className="space-y-1">
                                  <p className="text-base font-semibold">
                                    {place.displayName?.text ?? 'Untitled location'}
                                  </p>
                                  {address ? (
                                    <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                      {address}
                                    </p>
                                  ) : null}
                                </div>
                                <span className="text-xs font-semibold text-blue-500 flex items-center gap-1">
                                  {isSelected ? <Check className="h-4 w-4" /> : 'Select'}
                                </span>
                              </button>
                            );
                          })
                        ) : locationSearchQuery.trim().length >= 3 ? (
                          <p className={helperClass}>No locations found yet. Keep typing to refine.</p>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </div>

                {submitError ? (
                  <div className="rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-500">
                    {submitError}
                  </div>
                ) : null}

                <button
                  className={`${primaryButtonClass} ${
                    isCreateDisabled
                      ? isDarkMode
                        ? 'cursor-not-allowed pointer-events-none bg-blue-500/60 hover:bg-blue-500/60 focus:ring-0 opacity-70'
                        : 'cursor-not-allowed pointer-events-none bg-blue-600/60 hover:bg-blue-600/60 focus:ring-0 opacity-70'
                      : ''
                  }`}
                  type="submit"
                  disabled={isCreateDisabled}
                  title={!selectedPlaceId ? 'Select a location to continue' : undefined}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      Saving your organization...
                    </>
                  ) : (
                    <>
                      <Building2 className="h-4 w-4" aria-hidden="true" />
                      Create organization
                    </>
                  )}
                </button>
              </>
            )}
          </form>
        </section>
      </div>
    </DashboardLayout>
  );
}
